<template>
    <div class="edit-item-background">
        <div class="edit-item">
            <flex-row>
                <div class="col">
                    <items-typeahead
                        id="edit_item_sku"
                        name="edit_item_sku"
                        placeholder=""
                        spacing="xs"
                        :value="item.sku"
                        :label="$t('consumerOrders.labels.sku')"
                        :preloaded-options="items"
                        :allow-empty="false"
                        :use-templates="true"
                        :rules="{required: false}"
                        :validation="{name: $t('consumerOrders.labels.sku')}"
                        :disabled="isSkuEditDisabled"
                        @input="(value) => {
                            item.sku = value.sku;
                            item.hsCodeId = value.hsCodeId;
                            item.description = value.description;
                            item.availableQty = value.totalAvailableInventory;
                            item.selectedFromList = true;
                            item.isSerialTracked = value.isSerialTracked;
                            item.availableInventories = value.availableInventories;
                            item.isRecordSerialNumber = value.isSerialTracked;
                            item.isLotTracked = value.isLotTracked;
                            item.hazmatType = value.hazmatType;
                        }"
                        @skus-loaded="(loadedOptions) => saveSearchResult(loadedOptions)"
                    />
                </div>
                <div class="col col--width-qty">
                    <form-input
                        id="edit_item_qty"
                        v-model="item.quantity"
                        name="edit_item_qty"
                        :label="$t('consumerOrders.labels.quantity')"
                        spacing="xs"
                        type="number"
                        step="1"
                        min="1"
                        :rules="`required|min_value:1|max_value:${getAvailableCount()}|max:9`"
                        :validation="{name: $t('consumerOrders.labels.quantity')}"
                    />
                </div>
            </flex-row>
            <flex-row class="selected-item-info">
                <div class="item-info-section col">
                    <label class="info-section-label">
                        {{ $t('consumerOrders.labels.skuDescription') }}
                    </label>
                    <div
                        v-if="isLotTrackDetail"
                        class="info-section-disabled-text"
                    >
                        {{ item.description }}
                    </div>
                    <div
                        v-else
                        class="info-section-text"
                    >
                        {{ item.description }}
                    </div>
                </div>
                <div
                    v-if="!hideAvailableQty"
                    class="item-info-section col col--width-qty"
                >
                    <label class="info-section-label">
                        {{ $t('consumerOrders.labels.availableQty') }}
                    </label>
                    <div class="info-section-disabled-text">
                        {{ getAvailableCount() | valueOrDash }}
                    </div>
                </div>
                <div
                    v-if="isLotTrackDetail"
                    class="item-info-section col"
                >
                    <label class="info-section-label">
                        {{ $t('consumerOrders.labels.lotNumber') }}
                    </label>
                    <div class="info-section-disabled-text">
                        {{ item.lotNumber | valueOrDash }}
                    </div>
                </div>
                <div
                    v-if="isLotTrackDetail"
                    class="item-info-section col"
                >
                    <label class="info-section-label">
                        {{ $t('consumerOrders.labels.expiration') }}
                    </label>
                    <div class="info-section-disabled-text">
                        {{ item.expirationDate | date('short') | valueOrDash }}
                    </div>
                </div>
            </flex-row>
            <flex-row class="selected-item-info">
                <div v-if="isVisibleRecordSerialNumber && item.isSerialTracked"
                     class="item-info-section col"
                >
                    <base-radio-check id="is-record-serial-number"
                                      :value="item.isRecordSerialNumber"
                                      type="checkbox"
                                      :label="$t('items.labels.requiredSerialNumberCapture')"
                                      :tooltip-text="$t('items.labels.requiredSerialNumberCaptureToolTip')"
                                      @input="onRecordSerialNumber"
                    />
                </div>
            </flex-row>
            <flex-row class="selected-item-info">
                <div class="item-info-section col">
                    <base-button
                        id="editItem"
                        :primary="true"
                        :disabled="!item.sku || item.quantity == null || item.quantity < 1 || item.quantity > getAvailableCount()"
                        @click="saveItem"
                    >
                        {{ $t('consumerOrders.labels.saveSku') }}
                    </base-button>
                    <base-button
                        id="cancelEdit"
                        :primary="true"
                        class="cancel-edit"
                        @click="cancel"
                    >
                        Cancel
                    </base-button>
                </div>
            </flex-row>
        </div>
    </div>
</template>

<script>
import { BaseButton } from '@/components/buttons';
import { FormInput } from '@/components/form';
import ItemsTypeahead from '@/components/ItemsTypeahead';
import { BaseRadioCheck } from '@/components/base';
// eslint-disable-next-line
import { $t } from '@/translations';

export default {
    name: 'EditItemPopup',
    components: {
        BaseButton,
        FormInput,
        ItemsTypeahead,
        BaseRadioCheck,
    },
    props: {
        itemsList: {
            type: Object,
            required: true,
        },
        itemParent: {
            type: Object,
            required: false,
            default: null,
        },
        itemForEdit: {
            type: Object,
            required: true,
        },
        hideAvailableQty: {
            type: Boolean,
            default: false,
        },
        selectedWarehouse: {
            type: String,
            default: null,
        },
        isVisibleRecordSerialNumber: {
            type: Boolean,
            default: false,
        },
        isSkuEditDisabled: {
            type: Boolean,
            default: false,
        },
        isDisplayLotDetail: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        items() {
            return this.itemsList;
        },
        item() {
            return this.itemForEdit;
        },
        isLotTrackDetail() {
            return this.itemForEdit.isLotTracked && this.isDisplayLotDetail;
        },
        getAvailableCount() {
            return () => {
                if (this.selectedWarehouse == null || this.itemForEdit.availableInventories == null) {
                    return this.itemForEdit.availableQty;
                }

                const itemWarehouse = this.itemForEdit.availableInventories
                    .find(i => i.warehouseName.toUpperCase() === this.selectedWarehouse.toUpperCase());
                if (itemWarehouse) {
                    return itemWarehouse.availableInventoryQuantity;
                }

                return 0;
            };
        },
    },
    methods: {
        saveItem() {
            this.$emit('save-editing-item', this.itemForEdit, this.itemParent);
        },
        cancel() {
            this.$emit('cancel-editing-item');
        },
        async saveSearchResult(loadedOptions) {
            this.$emit('edit-popup-items-loaded', loadedOptions);
        },
        onRecordSerialNumber(checked) {
            this.itemForEdit.isRecordSerialNumber = checked;
        },
    },
};
</script>

<style lang="scss" scoped>
.edit-item-background {
    position: absolute;
    z-index: 60;
    top: 0;
    left: -50px;
    width: calc(100% + 100px);
    height: calc(100% - 4px);
    background-color: $color-white-opaque-75;
}

.edit-item {
    position: sticky;
    z-index: 61;
    top: 20%;
    width: calc(100% - 50px);
    margin: 0 25px;
    padding: 25px;
    border: 2px solid $color-gray-600;
    border-radius: $modal-border-radius;
    background-color: $color-white;

    .selected-item-info {
        margin-bottom: 20px;

        .item-info-section {
            margin: 0 5px;

            .info-section-label {
                display: inline-block;
                padding: 8px 0;
                color: $color-gray-400;
                font-size: $font-size-sm;
                font-weight: 500;
                line-height: $line-height-base;
                text-transform: uppercase;
            }

            .info-section-text {
                width: 100%;
                min-height: $input-height;
                padding: 9px 13px;
                border-radius: $input-border-radius;
                background-color: $color-teal-950;
                color: $color-gray-200;
                font-size: $font-size-base;
                word-break: break-word;
            }

            .info-section-disabled-text {
                width: 100%;
                min-height: $input-height;
                padding: 9px 13px;
                border-radius: $input-border-radius;
                background-color: $color-gray-800;
                color: $color-gray-200;
                font-size: $font-size-base;
                word-break: break-word;
            }
        }

        .item-empty-space {
            flex-grow: 0;
            min-width: 20px;
        }
    }

    .cancel-edit {
        margin-left: 20px;
    }
}
</style>
